import * as R from 'ramda';
import ImmutableMap from '../../language/ImmutableMap';
import { isNilOrEmpty } from '../../language/module';
import { isBetweenDates, toDateOnly, toUsDateFormat } from '../../language/time';
import { Resort } from '../../resortDetails/resort';
import { EntitlementHotlist, QuickEntitlement } from '../entitlements/models';
import { getGuestEntitlementIds } from '../entitlements/module';
import { GuestComment } from '../guestComments/guestComment';
import { GuestDetails } from '../guestDetails';
import { MAX_COMMENT_CHARS, MIN_COMMENT_CHARS } from './constants';
import { getResource } from '../../language/resources/getResource';
import { PhoneNumberUtil } from 'google-libphonenumber';


/**
 * Whether or not the given hotlist comments are valid.
 * Currently, valid is 10-500 chars, not undefined.
 */
export const isValidGuestComment = (
  comments: string | undefined,
): boolean => !!(comments && comments.length >= MIN_COMMENT_CHARS && comments.length <= MAX_COMMENT_CHARS);


/**
 * Validates a given submission to add/remove a guest to/from the hotlist (blacklist). Hotlisted guests will have a
 * Validates whether the comments for a hotlist addition/removal meet established requirements
 *
 * Returns an error message if the comments are invalid.  Returns undefined if comments are fine
 *
 * @param comments - The agent's comments for why this guest is being added e.g. 'Posed as a lift operator'
 */
export const validateHotlistComment = (
  resources: Record<string, any>,
  comments: string | undefined,
): string | undefined => {
  if (!isValidGuestComment((comments || '').trim())) {
    return getResource(resources, 'HOTLIST_NO_COMMENT');
  }

  return undefined;
};


/**
 * Validates a given submission to add a guest to the hotlist (blacklist). Hotlisted guests will have a
 * banner on their GuestCard and their pass won't work at the lift.
 *
 * Like any form, adding a guest to the hotlist requires specific details -- this function will return the
 * error message to surface to the user if something is wrong. If all is right, this function returns `undefined`.
 */
export const validateHotlistAdd = (
  resources: Record<string, any>,
  entitlement: QuickEntitlement | undefined,
  resort: Resort | undefined,
  effectiveDate: Date | undefined,
  expirationDate: Date | undefined,
  reason: string | number | undefined,
  comments: string | undefined,
  phoneNumberValue?: string | undefined,
  phoneNumberCountry?: string |undefined,
  phoneNumberExtension?: string | undefined,
): string | undefined => {
  if (entitlement === undefined) {
    return getResource(resources, 'HOTLIST_NO_ENTITLEMENT');
  }

  if (resort === undefined) {
    return getResource(resources, 'HOTLIST_NO_RESORT');
  }

  if (isNilOrEmpty(effectiveDate)) {
    return getResource(resources, 'HOTLIST_INVALID_EFF_DATE');
  }

  if (toDateOnly(new Date())! > toDateOnly(effectiveDate!)!) {
    return getResource(resources, 'HOTLIST_PREDATED_EFF_DATE');
  }

  if (isNilOrEmpty(expirationDate)) {
    return getResource(resources, 'HOTLIST_INVALID_EXP_DATE');
  }

  if (toDateOnly(effectiveDate!)! > toDateOnly(expirationDate!)!) {
    return getResource(resources, 'HOTLIST_EXPIRES_EFF_DATE');
  }

  if (isNilOrEmpty(reason as string | undefined)) {
    return getResource(resources, 'HOTLIST_NO_REASON');
  }

  if (typeof reason === 'number' && reason < 0) {
    return getResource(resources, 'HOTLIST_NO_REASON');
  }

  if (!isValidGuestComment((comments || '').trim())) {
    return getResource(resources, 'HOTLIST_NO_COMMENT');
  }

  if (phoneNumberValue && phoneNumberCountry && !isValidPhoneNumberForRegion(phoneNumberValue, phoneNumberCountry))
  {
    return getResource(resources, 'HOTLIST_MODAL_INVALID_PHONE_NUMBER_FORMAT');
  }

  if (!phoneNumberValue && phoneNumberExtension) {
    return getResource(resources, 'HOTLIST_MODAL_PHONE_NUMBER_REQUIRED');
  }
  
  return validateHotlistComment(resources, comments);
};

const phoneUtil = PhoneNumberUtil.getInstance();
const isValidPhoneNumberForRegion = (phone: string, country: string) => {
  try {
    const phoneNumber = phoneUtil.parse(phone, country);
      return phoneUtil.isValidNumberForRegion(phoneNumber, country);
  } catch (error) {
    return false;
  }
};

/**
 * Validates a given submission to remove a guest from the hotlist (blacklist). Hotlisted guests will have a
 * banner on their GuestCard and their pass won't work at the lift.
 *
 * Like any form, removing  a guest from the hotlist requires specific details -- this function will return the
 * error message to surface to the user if something is wrong. If all is right, this function returns `undefined`.
 *
 * @param comments - The agent's comments for why this guest is being added e.g. 'Posed as a lift operator'
 */
export const validateHotlistRemove = (
  resources: Record<string, any>,
  comments: string | undefined,
): string | undefined => validateHotlistComment(resources, comments);


/**
 * Determine if a given hotlist is active at present.
 */
export const isHotlistActive = (
  entitlementHotlist: EntitlementHotlist,
): boolean => entitlementHotlist.status === 'Active'
              && isBetweenDates(toDateOnly(new Date()), entitlementHotlist.effectiveDate, entitlementHotlist.expirationDate);


/**
 * Determine if a given hotlist is active now or will be in the future
 */
export const isHotlistFutureActive = (
  entitlementHotlist: EntitlementHotlist,
): boolean => entitlementHotlist.status === 'Active'
              && (
                isBetweenDates(toDateOnly(new Date()), entitlementHotlist.effectiveDate, entitlementHotlist.expirationDate)
                || isBetweenDates(entitlementHotlist.effectiveDate, toDateOnly(new Date()), entitlementHotlist.expirationDate));



/**
 * Determine if a given entitlement has an active hotlist for the given resort or will
 * have an active hotlist in the future
 */
export const isEntitlementHotlistedForResort = (
  ent: QuickEntitlement,
  res: Resort,
  entitlementHotlists?: ImmutableMap<EntitlementHotlist[]>,
): boolean => R.any(
  hotlist => isHotlistFutureActive(hotlist) && hotlist.resortAbbreviation === res.resortAbbreviation,
  entitlementHotlists?.get(ent.entitlementId.toString()) || [],
);


/**
 * Filter a map of entitlement hotlists down to an array of currently-active
 * entitlement hotlists
 *
 * @param entitlementHotlists - A map of entitlement hotlists which may be active or inactive
 */
export const getActiveHotlists = (
  entitlementHotlists?: ImmutableMap<EntitlementHotlist[]>,
): EntitlementHotlist[] => R.filter(
  isHotlistActive,
  R.flatten((entitlementHotlists || ImmutableMap.empty()).getValues()),
);


/**
 * Filter a map of entitlement hotlists down to an array of currently-active
 * entitlement hotlists
 *
 * @param entitlementHotlists - A map of entitlement hotlists which may be active or inactive
 */
export const getFutureActiveHotlists = (
  entitlementHotlists?: ImmutableMap<EntitlementHotlist[]>,
): EntitlementHotlist[] => R.filter(
  isHotlistFutureActive,
  R.flatten((entitlementHotlists || ImmutableMap.empty()).getValues()),
);


/**
 * Given a guest and a map of entitlement hotlists, returns only those hotlists which are active
 *  and which apply to entitlements held by that guest.
 * The vast majority of the time, you would expect this to return an empty array.
 */
export const getFutureActiveHotlistsForGuest = (
  guest: GuestDetails,
  entitlementHotlists?: ImmutableMap<EntitlementHotlist[]>,
): EntitlementHotlist[] => R.filter(
  hotlist => R.includes(hotlist.entitlementId, getGuestEntitlementIds(guest)),
  getFutureActiveHotlists(entitlementHotlists),
);


/**
 * Given a guest and a map of entitlement hotlists, returns only those hotlists which are active
 *  and which apply to entitlements held by that guest.
 * The vast majority of the time, you would expect this to return an empty array.
 */
export const getActiveHotlistsForGuest = (
  guest: GuestDetails,
  entitlementHotlists?: ImmutableMap<EntitlementHotlist[]>,
): EntitlementHotlist[] => R.filter(
  hotlist => R.includes(hotlist.entitlementId, getGuestEntitlementIds(guest)),
  getActiveHotlists(entitlementHotlists),
);


/**
 * Given a guest and a map of entitlement hotlists, returns all IDs for active entitlements held by the guest which are
 * currently hotlisted. This is useful e.g. in cases where entitlements must be distinguised on the basis of hotlist
 * status (such as in the pass overview table, where hotlisted entitlements are highlighted).
 */
export const getHotlistedActiveEntitlementIdsForGuest = (
  guest: GuestDetails,
  entitlementHotlists?: ImmutableMap<EntitlementHotlist[]>,
): number[] => R.uniq(
  R.map(hotlist => hotlist.entitlementId, getActiveHotlistsForGuest(guest, entitlementHotlists)),
);


/**
 * Determines whether any of the given hotlists are global hotlists which are currently active or will be active
 * in the future.
 */
export const hasNoFutureActiveGlobalHotlists = (
  hotlists: EntitlementHotlist[],
): boolean => R.none(hotlist => hotlist.isHotlistGlobal && isHotlistFutureActive(hotlist), hotlists);


/**
 * Determines whether any of the given hotlists are global hotlists which are active or will be active in the future.
 */
export const hasAnyFutureActiveGlobalHotlists = (
  hotlists: EntitlementHotlist[],
): boolean => R.any(hotlist => hotlist.isHotlistGlobal && isHotlistFutureActive(hotlist), hotlists);


/**
 * Determines whether any of the given hotlists are resort hotlists for the given resort and which are currently active
 * or will be active in the future.
 * @param entitlementHotlists
 * @param resortAbbreviation The abbreviation for the resort (e.g. SB, WP)
 */
export const hasNoFutureActiveHotlistsForResort = (
  entitlementHotlists: EntitlementHotlist[],
  resortAbbreviation: string,
): boolean =>
  R.none(
    entHotlist => !entHotlist.isHotlistGlobal
                  && isHotlistFutureActive(entHotlist)
                  && entHotlist.resortAbbreviation === resortAbbreviation,
    entitlementHotlists,
  );


/**
 * Determines whether any of the given hotlists are resort hotlists for the given resort and which will be active
 * in the future or are active now.
 * @param hotlists
 * @param resortAbbreviation The abbreviation for the resort (e.g. SB, WP)
 */
export const hasAnyFutureActiveHotlistsForResort = (
  hotlists: EntitlementHotlist[],
  resortAbbreviation: string,
): boolean => R.any(
  hotlist => !hotlist.isHotlistGlobal
             && isHotlistFutureActive(hotlist)
             && hotlist.resortAbbreviation === resortAbbreviation,
  hotlists,
);


/**
 * Returns a string representing the resort a hotlist applies to (or "All Resorts" if a global hotlist)
 * If the resort couldn't be found in resortList, returns the resortAbbreviation from the hotlist
 * instead. (e.g. SB, JU)
 */
export const getResortName = (
  hotEntry: EntitlementHotlist,
  resortList: Resort[],
): string => hotEntry.isHotlistGlobal
  ? 'All Resorts'
  : R.path(['resortName'], resortList.find(resort => resort.resortAbbreviation === hotEntry.resortAbbreviation))
    || hotEntry.resortAbbreviation
    || '';

/**
 * Creates a subject describing a hotlist entry based on the date range of the hotlist
 * and the resort
 */
export const getHotlistSubject = (
  hotEntry: EntitlementHotlist,
  resortName?: string,
): string => (resortName ? `${resortName} - ` : '')
             + `${toUsDateFormat(hotEntry.effectiveDate)} to ${toUsDateFormat(hotEntry.expirationDate)}`;


/**
 * Returns an array of comments representing all hotlisting and unhotlisting actions that have
 * occurred in the given list of entitlement hotlists
 * If there are no hotlists, returns an empty array.
 */
export const getHotlistComments = (
  guestId: string,
  entitlementHotlists: EntitlementHotlist[],
  resortList: Resort[],
): GuestComment[] => R.flatten(
  (entitlementHotlists || []).map(
    (hotEntry): GuestComment[] => R.concat(
      [
        {
          guestId:     guestId,
          updateDate:  String(hotEntry.createdDate),
          subjectText: 'HOTLISTED - ' + getHotlistSubject(hotEntry, getResortName(hotEntry, resortList || [])),
          commentText: hotEntry.hotlistComment || '',
          operatorId:  hotEntry.createdById || ''
        },
      ],
      isNilOrEmpty(hotEntry.unhotlistComment)
        ? []
        : [
          {
            guestId:     guestId,
            updateDate:  String(hotEntry.modifiedDate),
            subjectText: 'UNHOTLISTED - ' + getHotlistSubject(hotEntry, getResortName(hotEntry, resortList || [])),
            commentText: hotEntry.unhotlistComment || '',
            operatorId:  hotEntry.modifiedById || '',
          },
        ],
    ),
  ),
);

/**
 * Given an entitlement hotlist entry and a collection of resorts, returns the resort details associated with the
 * hotlist entry.
 *
 * Returns undefined if the resort key in the hotlist entry is not associated with any resort in the provided resort
 * collection.
 */
export const getHotlistResort = (
  hotlist: EntitlementHotlist,
  resorts: ImmutableMap<Resort>,
): Resort | undefined => !resorts || !hotlist ? undefined : resorts.get(hotlist.resortAbbreviation || '');

/**
 * Given a map of entitlement hotlist entries and a list of resorts, returns a map containing only the hotlist
 * entries which apply to resorts in the given resort list.
 */
export const filterHotlistsByResortList = (
  hotlists: ImmutableMap<EntitlementHotlist[]>,
  resorts?: Resort[],
): ImmutableMap<EntitlementHotlist[]> =>
  // Some entitlementIds will have all of their hotlists removed resulting in an empty array.
  // It's important to retain that entry with the empty array so we know we've retrieved the hotlists from the API.
  hotlists.mapInto((entitlementId: string, hotlistList: EntitlementHotlist[]) =>
    hotlistList.filter(hotlist => R.any(res => hotlist.resortAbbreviation === res.resortAbbreviation, resorts || [])),
  );
